import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, Lock } from "@phosphor-icons/react";
import logo from "assests/logos/small-logo.png";
import { videoThumb } from "Utilities/courseImages";
import { getPlaceholderGradient } from "Utilities/placeholderColor";

// One episode in the series grid — poster with number/state overlays and a
// Dee Class play button on hover. Whole card links to the series player, or
// to /plans when the viewer has no access.
const EpisodeCard = ({ episode, index, hasAccess, slug }) => {
  const { t, i18n } = useTranslation();
  const { title, description, series_video_id } = episode;
  const isRTL = i18n.language === "ar";

  const done = hasAccess && (episode.is_done || series_video_id?.is_done);
  const thumb = videoThumb(series_video_id);
  const number = isRTL
    ? (index + 1).toLocaleString("ar-EG")
    : String(index + 1).padStart(2, "0");
  const to = hasAccess ? `/course/watch-series/${slug}` : "/plans";

  return (
    <Link
      to={to}
      className={`group block rounded-2xl overflow-hidden bg-grey border transition-all duration-300 hover:-translate-y-1 ${
        done
          ? "border-green-500/50 hover:border-green-400"
          : "border-white/5 hover:border-white/15"
      }`}
    >
      <div
        className="relative aspect-video overflow-hidden"
        style={{ background: getPlaceholderGradient(series_video_id?._id || index) }}
      >
        {thumb && (
          <img
            src={thumb}
            alt={title}
            loading="lazy"
            decoding="async"
            className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${
              hasAccess ? "" : "saturate-[.4] brightness-[.55]"
            }`}
          />
        )}

        <div
          className={`absolute inset-0 bg-gradient-to-t ${
            done ? "from-green-950/70" : "from-black/70"
          } via-transparent to-transparent pointer-events-none`}
        />

        <span
          className={`absolute top-3 ${isRTL ? "right-4" : "left-4"} text-3xl font-extrabold leading-none ${
            done ? "text-white/70" : "text-white"
          }`}
          style={{ textShadow: "0 2px 12px rgba(0,0,0,.6)" }}
        >
          {number}
        </span>

        <div className={`absolute top-3 ${isRTL ? "left-3" : "right-3"} z-10`}>
          {done ? (
            <span className="grid place-items-center w-8 h-8 rounded-full bg-green-500 shadow-lg shadow-green-500/40 ring-4 ring-green-500/25">
              <CheckCircle size={20} weight="fill" className="text-white" />
            </span>
          ) : !hasAccess ? (
            <span className="grid place-items-center w-7 h-7 rounded-full bg-black/60 backdrop-blur-sm">
              <Lock size={15} weight="fill" className="text-white" />
            </span>
          ) : null}
        </div>

        {done && (
          <span
            className={`absolute bottom-3 ${isRTL ? "right-3" : "left-3"} inline-flex items-center gap-1.5 bg-green-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-lg shadow-green-500/40`}
          >
            <CheckCircle size={13} weight="fill" />
            {t("courses.completed")}
          </span>
        )}

        <span className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <span className="grid place-items-center w-14 h-14 rounded-full bg-white shadow-xl shadow-primary/40 scale-90 group-hover:scale-100 transition-transform duration-300">
            <img src={logo} alt="" className="w-7 h-7 ms-0.5" />
          </span>
        </span>
      </div>

      <div className="p-4">
        <h3
          className={`text-base font-bold mb-1 line-clamp-1 ${
            done ? "text-green-400" : "text-white group-hover:text-primary transition-colors"
          }`}
        >
          {number}. {title}
        </h3>
        <p className="text-lightWhite text-sm line-clamp-2">{description}</p>
      </div>
    </Link>
  );
};

export default EpisodeCard;
